import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericTablePage from '../components/GenericTablePage';
import { GridColDef } from '@mui/x-data-grid';
import { Chip } from '@mui/material';
import { formatDate } from '../lib/dateUtils';
import { enumChip } from '../lib/enumLabels';

const TrainingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  const columns: GridColDef[] = [
    { field: 'title', headerName: t('trainings.trainingTitle'), flex: 1, minWidth: 250 },
    { field: 'location', headerName: t('trainings.location'), width: 180 },
    {
      field: 'status',
      headerName: t('trainings.status'),
      width: 130,
      renderCell: (params) => {
        const chip = enumChip('status', params.value, locale);
        return chip ? <Chip label={chip.label} color={chip.color} size="small" /> : null;
      },
    },
    {
      field: 'is_public',
      headerName: t('trainings.isPublic'),
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common.yes') : t('common.no')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'needs_review',
      headerName: t('trainings.needsReview'),
      width: 130,
      sortable: false,
      renderCell: (params) =>
        params.value ? <Chip label={t('trainings.needsReview')} color="warning" size="small" /> : null,
    },
    {
      field: 'date',
      headerName: t('trainings.date'),
      width: 150,
      valueFormatter: (value) => formatDate(value, locale, 'medium'),
    },
    {
      field: 'registration_deadline',
      headerName: t('trainings.registrationDeadline'),
      width: 150,
      valueFormatter: (value) => formatDate(value, locale, 'medium'),
    },
  ];

  const fields = [
    {
      name: 'title',
      label: t('trainings.trainingTitle'),
      type: 'text' as const,
      required: true,
      width: 'full' as const,
    },
    {
      name: 'description',
      label: t('trainings.description'),
      type: 'textarea' as const,
      width: 'full' as const,
    },
    {
      name: 'date',
      label: t('trainings.trainingDate'),
      type: 'date' as const,
      required: true,
      width: 'third' as const,
    },
    {
      name: 'registration_deadline',
      label: t('trainings.registrationDeadline'),
      type: 'date' as const,
      width: 'third' as const,
    },
    {
      name: 'location',
      label: t('trainings.location'),
      type: 'text' as const,
      width: 'third' as const,
    },
    {
      name: 'instructor_name',
      label: t('trainings.instructorName'),
      type: 'text' as const,
      width: 'third' as const,
    },
    {
      name: 'cost',
      label: t('trainings.cost'),
      type: 'number' as const,
      width: 'third' as const,
    },
    {
      name: 'cost_currency',
      label: t('trainings.costCurrency'),
      type: 'text' as const,
      width: 'third' as const,
    },
    {
      name: 'status',
      label: t('trainings.status'),
      type: 'select' as const,
      width: 'third' as const,
      options: [
        { value: 'upcoming', label: t('trainings.upcoming') },
        { value: 'ongoing', label: t('trainings.ongoing') },
        { value: 'completed', label: t('trainings.completed') },
        { value: 'cancelled', label: t('trainings.cancelled') },
      ],
    },
    {
      name: 'source',
      label: t('trainings.source'),
      type: 'text' as const,
      width: 'third' as const,
    },
    {
      name: 'source_url',
      label: t('trainings.sourceUrl'),
      type: 'text' as const,
      width: 'third' as const,
    },
    {
      name: 'announced_date',
      label: t('trainings.announcedDate'),
      type: 'date' as const,
      width: 'third' as const,
    },
    {
      name: 'is_public',
      label: t('trainings.isPublic'),
      type: 'boolean' as const,
      width: 'third' as const,
    },
    {
      name: 'needs_review',
      label: t('trainings.needsReview'),
      type: 'boolean' as const,
      width: 'third' as const,
    },
    {
      name: 'data_notes',
      label: t('trainings.dataNotes'),
      type: 'textarea' as const,
      width: 'full' as const,
    },
    {
      name: 'flyer_url',
      label: t('trainings.flyer'),
      type: 'image' as const,
      bucket: 'gallery',
      width: 'full' as const,
    },
  ];

  return (
    <GenericTablePage
      tableName="trainings"
      title={t('trainings.title')}
      columns={columns}
      fields={fields}
      detailPath={(id) => `/trainings/${id}`}
      editPath={(id) => (id ? `/trainings/${id}/edit` : '/trainings/new')}
    />
  );
};

export default TrainingsPage;
